import { teachingMaterialQueryKeys } from "@/api/query-keys/teachingMaterials"
import {
  TEACHING_MATERIAL_STORAGE_KEY,
  useTeachingMaterialFilters,
} from "@/hooks/filters/useTeachingMaterialFilters"
import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import {
  DataTable,
  selectColumn,
  createSortableColumn,
  TABLE_PAGE_SIZE,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  createCustomColumn,
  formatPriceFromApi,
  createActionsColumn,
  useExportMutation,
  teachingMaterialTypeDropdownOptions,
  teachingMaterialOwnerMap,
  Button,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import type { TeachingMaterial } from "toptaal-shared"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { useState } from "react"
import { EditTeachingMaterialModal } from "@/components/modals/teaching-materials/EditTeachingMaterialModal"
import DeleteTeachingMaterialModal from "@/components/modals/teaching-materials/DeleteTeachingMaterialModal"
import ArchiveTeachingMaterialModal from "@/components/modals/teaching-materials/ArchiveTeachingMaterialModal"
import UnarchiveTeachingMaterialModal from "@/components/modals/teaching-materials/UnarchiveTeachingMaterialModal"
import { z } from "zod"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"

export const Route = createFileRoute("/beheer/lesmaterialen/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "teachingMaterials"),
  validateSearch: createTableSearchSchema().extend({
    archived: z.boolean().optional().catch(false),
  }),
})

const DEFAULT_COLUMN_VISIBILITY = {
  owner: false,
}

const ALL_COLUMN_IDS = ["id", "title", "price", "type", "owner"]

function RouteComponent() {
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<TeachingMaterial | null>(null)

  const searchParams = useSearch({ from: "/beheer/lesmaterialen/" })
  const navigate = useNavigate({
    from: "/beheer/lesmaterialen/",
  })

  const { archived } = searchParams

  const { filterItems, activeFilters, queryFn } = useTeachingMaterialFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    archived,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
    queryKeyMerged,
  } = usePaginatedTableWithUrlState({
    queryKey: [...teachingMaterialQueryKeys.all, activeFilters, { archived }],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: TEACHING_MATERIAL_STORAGE_KEY,
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const columns: ColumnDef<TeachingMaterial>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({ accessorKey: "title", name: "Titel" }),
    createCustomColumn({
      cell: ({ row }) => formatPriceFromApi(row.original.price),
      accessorKey: "price",
      name: "Prijs",
      id: "price",
    }),
    createCustomColumn({
      cell: ({ row }) =>
        teachingMaterialTypeDropdownOptions.find(
          (option) => option.value === row.original.type,
        )?.label,
      accessorKey: "type",
      name: "Type",
      id: "type",
    }),
    createCustomColumn({
      cell: ({ row }) => teachingMaterialOwnerMap[row.original.owner] || "",
      accessorKey: "owner",
      name: "Eigendom",
      id: "owner",
    }),
    createActionsColumn([
      {
        label: "Lesmateriaal wijzigen",
        onClick: (e, teachingMaterial) => {
          e.stopPropagation()
          setActiveRow(teachingMaterial)
          setIsEditOpen(true)
        },
      },
      {
        label: "Lesmateriaal verwijderen",
        onClick: (e, teachingMaterial) => {
          e.stopPropagation()
          setActiveRow(teachingMaterial)
          setIsDeleteOpen(true)
        },
      },
      ...(archived
        ? [
            {
              label: "Lesmateriaal herstellen",
              onClick: (
                e: React.MouseEvent,
                teachingMaterial: TeachingMaterial,
              ) => {
                e.stopPropagation()
                setActiveRow(teachingMaterial)
                setIsUnarchiveOpen(true)
              },
            },
          ]
        : [
            {
              label: "Lesmateriaal archiveren",
              onClick: (
                e: React.MouseEvent,
                teachingMaterial: TeachingMaterial,
              ) => {
                e.stopPropagation()
                setActiveRow(teachingMaterial)
                setIsArchiveOpen(true)
              },
            },
          ]),
    ]),
  ]

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
        page: 1,
      }),
    })
  }

  const { mutate: exportLessonMaterials } = useExportMutation({
    exportApiPrefix: "teaching_materials",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title={archived ? "Lesmaterialen archief" : "Lesmaterialen"}
        data={data?.data.teachingMaterials || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              onClick={handleToggleArchived}
              variant={archived ? "primary" : "secondary"}
              icon={<ArchiveBoxIcon className="size-5" />}
            >
              Archief
            </Button>
          </>
        }
        headerRightActions={
          <>
            <TableHeaderActions
              exportAction={{
                onClick: exportLessonMaterials,
              }}
            />

            <TableHeaderActions
              createAction={{
                label: "Nieuw lesmateriaal",
                to: "/beheer/lesmaterialen/aanmaken",
                entity: "teachingMaterials",
                permissions: "create",
              }}
            />
          </>
        }
      />

      {isArchiveOpen && activeRow && (
        <ArchiveTeachingMaterialModal
          isOpen={isArchiveOpen}
          setIsOpen={setIsArchiveOpen}
          teachingMaterial={activeRow}
        />
      )}

      {isUnarchiveOpen && activeRow && (
        <UnarchiveTeachingMaterialModal
          isOpen={isUnarchiveOpen}
          setIsOpen={setIsUnarchiveOpen}
          teachingMaterial={activeRow}
        />
      )}

      {isDeleteOpen && activeRow && (
        <DeleteTeachingMaterialModal
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          teachingMaterial={activeRow}
          queryKey={queryKeyMerged}
        />
      )}

      <EditTeachingMaterialModal
        isEditOpen={isEditOpen}
        setIsEditOpen={setIsEditOpen}
        activeRow={activeRow}
        closeDialog={() => setIsEditOpen(false)}
      />
    </ScreenWrapper>
  )
}
